import { createContext, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { FormAutoSubmit } from "../../components/Form/AutoSubmit";

/**
 * @template [TFields=any]
 * @template [TResponse=any]
 * @typedef {object} SearchInfiniteContextValue
 * @property {import("swr/infinite").SWRInfiniteResponse<TResponse>} swrInfiniteResponse
 * @property {() => void} reset
 * @property {string[]} appliedFilters
 * @property {() => void} submit
 * @property {import("react-hook-form").UseFormReturn<TFields>} form
 * @property {boolean} autoSubmit
 * @property {TFields} submittedFields
 * @property {boolean} isDirty
 */

/** @type {Partial<SearchInfiniteContextValue>} */
const SEARCH_INFINITE_CONTEXT_DEFAULT_VALUE = {
  swrInfiniteResponse: undefined,
  reset: () => {},
  appliedFilters: [],
  submit: () => {},
  form: undefined,
  autoSubmit: false,
  submittedFields: {},
  isDirty: false,
};

export const SearchInfiniteContext = createContext(
  /** @type {SearchInfiniteContextValue} */ (
    SEARCH_INFINITE_CONTEXT_DEFAULT_VALUE
  ),
);

/**
 * @typedef {object} Props
 * @property {import("react").ReactNode} children
 *
 * @param {Props & SearchInfiniteContextValue} props
 */
function SearchInfiniteProvider({
  form,
  appliedFilters,
  reset,
  submit,
  swrInfiniteResponse,
  autoSubmit,
  submittedFields,
  isDirty,
  children,
}) {
  const value = useMemo(() => {
    /** @type {SearchInfiniteContextValue} */
    const value = {
      appliedFilters,
      form,
      reset,
      submit,
      swrInfiniteResponse,
      autoSubmit,
      submittedFields,
      isDirty,
    };
    return value;
  }, [
    appliedFilters,
    autoSubmit,
    form,
    isDirty,
    reset,
    submit,
    submittedFields,
    swrInfiniteResponse,
  ]);

  return (
    <FormProvider {...form}>
      <SearchInfiniteContext.Provider value={value}>
        {autoSubmit && <FormAutoSubmit onSubmit={submit} />}

        {children}
      </SearchInfiniteContext.Provider>
    </FormProvider>
  );
}

export default SearchInfiniteProvider;
